<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createS3Config } from "./fs";
  import { makeFileSystemS3, testS3Connection } from "./fs-s3";
  import { telegramNotify, throwIfFetchFailed } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let endpointPlaceholder = "s3.amazonaws.com";

  let initConfig = createS3Config();
  let config = $state(initConfig);
  let testingConnection = $state(false);
  let errorMessage = $state("");

  // name is optional
  let canConnect = $derived(config.secret && config.access && config.bucket && !testingConnection);

  async function onConnect() {
    console.log("onConnect()");
    if (config.endpoint == "") {
      config.endpoint = endpointPlaceholder;
    }
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing s3 connection");
    try {
      let rsp = await testS3Connection(config);
      console.log("rsp", rsp);
      await throwIfFetchFailed(rsp);
      removeMsg();
      let fs = makeFileSystemS3(config);
      const msg = `New S3 connection ${config.bucket}.${config.endpoint}`;
      telegramNotify(msg);

      mountfs(fs);
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect S3">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-key">S3 Access Key:</label>
      <input
        type="text"
        id="input-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.access}
      />

      <label class="mt-3" for="input-secret">S3 Secret:</label>
      <input
        type="text"
        id="input-secret"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.secret}
      />

      <label class="mt-3" for="input-bucket">S3 Bucket:</label>
      <input
        type="text"
        id="input-bucket"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.bucket}
      />

      <label class="mt-3" for="input-endpoint">Endpoint:</label>
      <input
        type="text"
        id="input-endpoint"
        autocomplete="off"
        placeholder={endpointPlaceholder}
        class="outline-hidden border px-2 py-1 text-sm"
        bind:value={config.endpoint}
      />

      <div class="mt-4 text-sm">
        <a href="/doc/S3" class="text-blue-600 underline" target="_blank" rel="noreferrer">configuration help</a>
      </div>

      {#if testingConnection}
        <div class="text-sm text-gray-500 mt-2 mb-2">testing s3 settings...</div>
      {/if}

      {#if errorMessage}
        <div class="mt-2 mb-2 text-red-500 font-medium break-all w-72">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect S3</button>
    </div>
  {/snippet}
</DialogBase>
